if (!customElements.get('lz-catalog-product')) {
  const formatMoney = (cents) =>
    new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(cents / 100);

  customElements.define(
    'lz-catalog-product',
    class extends HTMLElement {
      connectedCallback() {
        // pageshow (Zurück-Navigation) bei jedem Einfügen neu binden, Element-Listener nur einmal
        if (!this.restore) {
          this.restore = (e) => {
            const v = this.selectedVariant;
            if (v) this.updateVariant(v, false);
            // Nach Zurück-Navigation kann sich der Warenkorb geändert haben
            if (e && e.persisted && this.hasAttribute('data-cart')) this.refreshCart();
          };
        }
        window.addEventListener('pageshow', this.restore);
        if (this.listenersBound) return;
        this.listenersBound = true;

        this.addEventListener('click', (e) => {
          const step = e.target.closest('button[data-qty-step]');
          if (step && this.contains(step)) {
            const input = this.quantityInput;
            if (!input) return;
            const next = (parseInt(input.value, 10) || 1) + Number(step.dataset.qtyStep);
            input.value = String(Math.min(Number(input.max) || 99, Math.max(1, next)));
            this.updateTotal();
            return;
          }

          // Vergleichskarte: Ausführung auswählen und zum Kaufbereich springen
          const pick = e.target.closest('button[data-select-variant]');
          if (pick && this.contains(pick)) {
            const radio = this.querySelector('input[data-lz-variant][value="' + pick.dataset.selectVariant + '"], input[name="id"][value="' + pick.dataset.selectVariant + '"]');
            if (radio) {
              radio.checked = true;
              radio.dispatchEvent(new Event('change', { bubbles: true }));
              const form = radio.closest('form');
              form?.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'center' });
              radio.focus({ preventScroll: true });
            }
            return;
          }

          const videoThumb = e.target.closest('button[data-show-video]');
          if (videoThumb && this.contains(videoThumb)) {
            this.showVideo();
            return;
          }

          const b = e.target.closest('button[data-image]');
          if (!b || !this.contains(b)) return;
          this.showImage(
            b.dataset.image,
            b.dataset.alt || 'Produktbild ' + b.dataset.index + ' / ' + this.querySelectorAll('button[data-image]').length,
            b.dataset.srcset
          );
          this.querySelectorAll('.thumbs button').forEach((t) => t.setAttribute('aria-pressed', String(t === b)));
        });

        this.addEventListener('change', (e) => {
          if (e.target.matches('[name="quantity"]')) {
            this.normalizeQuantity();
            this.updateTotal();
            return;
          }
          if (!e.target.matches('[name="id"], [data-lz-variant]')) return;
          const v = e.target.tagName === 'SELECT' ? e.target.selectedOptions[0] : e.target;
          this.updateVariant(v, true);
          // Kein history.replaceState: das Meta-Pixel wertet URL-Änderungen als zusätzlichen PageView
        });

        this.addEventListener('input', (e) => {
          if (e.target.matches('[name="quantity"]')) this.updateTotal();
        });

        this.setupAutoplayVideos();
        this.setupStickyBar();
      }

      // Mobile Kaufleiste: erscheint erst, wenn der Kaufbutton nach oben aus dem Bild gescrollt ist,
      // und verschwindet am Ende des Produktbereichs (Footer, Cookie-Hinweise bleiben frei)
      setupStickyBar() {
        const bar = this.querySelector('[data-sticky]');
        const buy = this.querySelector('form button[name="add"]');
        const end = this.querySelector('[data-sticky-end]');
        if (!bar || !buy || !('IntersectionObserver' in window)) return;
        let pastBuy = false;
        let atEnd = false;
        const render = () => { bar.hidden = !(pastBuy && !atEnd); };
        new IntersectionObserver(([entry]) => {
          pastBuy = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          render();
        }).observe(buy);
        if (end) {
          new IntersectionObserver(([entry]) => {
            atEnd = entry.isIntersecting || entry.boundingClientRect.top < 0;
            render();
          }).observe(end);
        }
      }

      async refreshCart() {
        try {
          const res = await fetch((window.Shopify?.routes?.root || '/') + 'cart.js', { headers: { Accept: 'application/json' } });
          if (!res.ok) return;
          const cart = await res.json();
          this.dataset.cart = String(cart.total_price || 0);
          this.dataset.cartCount = String(cart.item_count || 0);
          this.updateTotal();
        } catch (e) {}
      }

      disconnectedCallback() {
        window.removeEventListener('pageshow', this.restore);
      }

      get quantityInput() {
        return this.querySelector('input[name="quantity"]');
      }

      get quantity() {
        return Math.max(1, parseInt(this.quantityInput?.value, 10) || 1);
      }

      get selectedVariant() {
        const s = this.querySelector('select[name="id"]');
        if (s) return s.selectedOptions[0];
        return this.querySelector('input[data-lz-variant]:checked') || this.querySelector('input[name="id"]:checked, input[type="hidden"][name="id"]');
      }

      normalizeQuantity() {
        const input = this.quantityInput;
        if (input) input.value = String(Math.min(Number(input.max) || 99, this.quantity));
      }

      showImage(src, label, srcset) {
        const img = this.querySelector('[data-main-image]');
        if (srcset) {
          img.srcset = srcset;
          img.sizes = '(min-width: 750px) 50vw, 100vw';
        } else {
          img.removeAttribute('srcset');
          img.removeAttribute('sizes');
        }
        img.src = src;
        img.alt = this.dataset.product + ' – ' + label;
        img.hidden = false;
        const videoWrap = this.querySelector('[data-main-video]');
        if (videoWrap) {
          videoWrap.hidden = true;
          videoWrap.querySelector('video')?.pause();
        }
        this.querySelector('[data-count]').textContent = label;
      }

      // Anwendungsvideo im Galeriebereich anzeigen (stumm, mit Bedienelementen)
      showVideo() {
        const videoWrap = this.querySelector('[data-main-video]');
        if (!videoWrap) return;
        this.querySelector('[data-main-image]').hidden = true;
        videoWrap.hidden = false;
        this.querySelector('[data-count]').textContent = 'Video';
        this.querySelectorAll('.thumbs button').forEach((t) => t.setAttribute('aria-pressed', String(t.hasAttribute('data-show-video'))));
        const video = videoWrap.querySelector('video');
        if (video && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          video.muted = true;
          video.play().catch(() => {});
        }
      }

      // Bilder, Lieferumfang und Vergleich passend zur gewählten Ausführung anzeigen
      applyGroup(group) {
        if (!group) return;
        this.dataset.group = group;
        this.querySelectorAll('.thumbs button[data-media-group]').forEach((t) => {
          const g = t.dataset.mediaGroup;
          t.hidden = !(g === 'all' || g === group);
        });
        this.querySelectorAll('[data-variant-panel]').forEach((panel) => {
          panel.hidden = panel.dataset.variantPanel !== group;
        });
        this.querySelectorAll('[data-compare]').forEach((card) => {
          card.classList.toggle('is-selected', card.dataset.compare === group);
        });
      }

      // Videos mit data-lz-autoplay nur abspielen, solange sie sichtbar sind; nie bei reduzierter Bewegung
      setupAutoplayVideos() {
        const videos = [...this.querySelectorAll('video[data-lz-autoplay]')];
        if (!videos.length) return;
        videos.forEach((v) => {
          v.muted = true;
          v.setAttribute('playsinline', '');
        });
        const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
        reduced.addEventListener?.('change', () => {
          if (reduced.matches) videos.forEach((v) => { v.lzInView = false; v.pause(); });
        });
        if (!('IntersectionObserver' in window)) return;
        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              const video = entry.target;
              // Erst ab 50 % Sichtbarkeit abspielen, darunter pausieren
              const visible = entry.isIntersecting && entry.intersectionRatio >= 0.5;
              video.lzInView = visible;
              if (visible && !reduced.matches && !video.dataset.userPaused && video.offsetParent !== null) {
                video.play().catch(() => {});
              } else if (!visible) {
                video.pause();
              }
            });
          },
          { threshold: [0, 0.5] }
        );
        videos.forEach((v) => {
          // Wer selbst pausiert, bekommt kein erneutes Autoplay
          v.addEventListener('pause', () => {
            // Nur als Nutzer-Pause werten, wenn das Video sichtbar war (nicht durch Wegscrollen pausiert)
            if (v.lzInView && v.offsetParent !== null && !v.ended && document.visibilityState === 'visible') {
              v.dataset.userPaused = '1';
            }
          });
          v.addEventListener('play', () => delete v.dataset.userPaused);
          observer.observe(v);
        });
      }

      // Betrag im Shop-Format ausgeben: data-money-sample enthält 1.234,56 im Format von {{ 123456 | money }}
      formatMoney(cents) {
        const sample = this.dataset.moneySample;
        if (sample && sample.includes('1.234,56')) {
          const amount = new Intl.NumberFormat('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(cents / 100);
          return sample.replace('1.234,56', amount);
        }
        return formatMoney(cents);
      }

      // Gesamtpreis inkl. Versand für die gewählte Variante und Menge (Werte aus den Theme-Einstellungen)
      updateTotal() {
        const v = this.selectedVariant;
        const total = this.querySelector('.delivery [data-total]');
        if (!v || !total) return;
        const ship = Number(this.dataset.ship) || 0;
        const free = Number(this.dataset.free) || 0;
        const subtotal = Number(v.dataset.cents) * this.quantity;
        const qtyLabel = this.querySelector('[data-qty-label]');
        if (qtyLabel) qtyLabel.textContent = String(this.quantity);

        // Zusammenfassung: Versandgrenze berücksichtigt die Auswahl und den vorhandenen Warenkorb.
        if (this.querySelector('[data-summary]')) {
          const inCart = Number(this.dataset.cart) || 0;
          const isFree = subtotal + inCart >= free;
          total.textContent = isFree
            ? 'Deine Auswahl: ' + this.formatMoney(subtotal) + ' · Versand kostenlos'
            : 'Deine Auswahl inkl. Versand: ' + this.formatMoney(subtotal + ship) + ' (' + this.formatMoney(subtotal) + ' + ' + this.formatMoney(ship) + ' Versand)';
          const shipText = isFree ? 'Versandkostenfrei innerhalb Deutschlands' : 'zzgl. ' + this.formatMoney(ship) + ' Versand';
          const shipLine = this.querySelector('[data-ship-line]');
          if (shipLine) shipLine.textContent = shipText;
          const stickyShip = this.querySelector('[data-sticky-ship]');
          if (stickyShip) stickyShip.textContent = isFree ? 'Versand kostenlos' : shipText;
          const title = this.querySelector('[data-summary-title]');
          if (title) title.textContent = v.dataset.title;
          const freeHint = this.querySelector('[data-free-hint]');
          if (freeHint) freeHint.hidden = isFree;
          const cartHint = this.querySelector('[data-cart-hint]');
          if (cartHint) {
            const show = subtotal < free && inCart > 0 && subtotal + inCart >= free;
            cartHint.hidden = !show;
            if (show) {
              cartHint.textContent =
                'Zusammen mit deinem Warenkorb (' + this.formatMoney(inCart) + ') erreichst du ' + this.formatMoney(free) +
                ' – dann ist der Versand für die ganze Bestellung kostenlos.';
            }
          }
          return;
        }

        const inCart = Number(this.dataset.cart) || 0;
        total.textContent =
          subtotal + inCart >= free
            ? this.formatMoney(subtotal) + ' inklusive kostenlosem Versand'
            : this.formatMoney(subtotal + ship) + ' inklusive ' + this.formatMoney(ship) + ' Versand';
      }

      updateVariant(v, changeImage) {
        this.querySelector('.price[data-price]').textContent = v.dataset.price;
        const included = this.querySelector('[data-included]');
        if (included) included.textContent = 'Deine Auswahl: ' + v.dataset.title + '.';
        // Verstecktes Formularfeld mit der gewählten Ausführung synchron halten
        const idField = this.querySelector('input[type="hidden"][data-lz-variant-id]');
        if (idField && v.value) idField.value = v.value;
        this.updateTotal();
        const b = this.querySelector('button[name="add"]');
        b.disabled = v.dataset.available !== 'true';
        // Mit Zusammenfassung steht der Preis bereits daneben – Button bleibt kurz
        const shortLabel = !!this.querySelector('[data-summary]');
        b.textContent = b.disabled ? 'Ausverkauft' : shortLabel ? 'In den Warenkorb' : 'In den Warenkorb · ' + v.dataset.price;
        const sticky = this.querySelector('[data-sticky]');
        if (sticky) {
          sticky.querySelector('[data-sticky-title]').textContent = v.dataset.title;
          sticky.querySelector('[data-sticky-price]').textContent = v.dataset.price;
          const sb = sticky.querySelector('[data-sticky-add]');
          sb.disabled = b.disabled;
          sb.textContent = b.disabled ? 'Ausverkauft' : 'In den Warenkorb';
        }
        const express = this.querySelector('.express');
        if (express) express.hidden = b.disabled;
        if (v.dataset.group) this.applyGroup(v.dataset.group);
        if (changeImage) {
          const play = this.querySelector('.play-photo');
          if (play) {
            const photo = play.querySelector('img');
            photo.src = v.dataset.image || this.dataset.overview;
            photo.removeAttribute('srcset');
            photo.alt = this.dataset.product + ' – ' + v.dataset.title;
            play.querySelector('figcaption').textContent =
              'Produktansicht · ' + v.dataset.title + '. Maßgeblich ist der Lieferumfang der ausgewählten Ausführung.';
          }
          const shown = v.dataset.image || this.dataset.overview;
          this.querySelectorAll('.thumbs button').forEach((t) => t.setAttribute('aria-pressed', String(t.dataset.image === shown)));
          this.showImage(shown, v.dataset.image ? 'Ausgewählte Ausführung' : 'Produktübersicht', v.dataset.srcset);
        }
      }
    }
  );
}
